<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import TieredPricingCards from '@/Components/Forms/TieredPricingCards.vue'
import RevenueCalculator from './RevenueCalculator.vue'
import AvailabilityScheduler from '@/Components/Forms/AvailabilityScheduler.vue'
import SubjectLevelSelector from '@/Components/Forms/SubjectLevelSelector.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'

const props = defineProps({
  pricingLimits: {
    type: Object,
    default: () => ({
      min_price_basic: 12,
      min_price_silver: 25,
      min_price_gold: 45,
    }),
  },
  maxGroupSessionsPerCourse: {
    type: Number,
    default: 4
  },
  maxOneToOneSessions: {
    type: Number,
    default: 10
  },
  userSubjects: {
    type: Array,
    default: () => []
  },
  userLevels: {
    type: Array,
    default: () => []
  },
  teachingMethodIds: {
    type: Array,
    default: () => []
  },
  teachingMethods: {
    type: Array,
    default: () => []
  },
  application: {
    type: Object,
    default: null
  },
  slug: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['next', 'back'])

const loading = ref(false)
const errors = ref({})

// Pricing state - initialize with minimum prices from server
const pricing = ref({
  basic: props.pricingLimits.base?.min || 12,
  silver: props.pricingLimits.silver?.min || 25,
  gold: props.pricingLimits.gold?.min || 45
})

// Selected subjects for group tutoring
const selectedGroupSubjects = ref([])

// Current subject-level being scheduled for group sessions
const currentSubjectLevel = ref(null)

// Availability state
const selectedGroupSlots = ref([])
const selectedOneOnOneSlots = ref([])

// Function to populate data from application
const populateFromApplication = () => {
  if (!props.application) return

  if (props.application.pricing) {
    if (props.application.pricing.base) pricing.value.basic = props.application.pricing.base
    if (props.application.pricing.silver) pricing.value.silver = props.application.pricing.silver
    if (props.application.pricing.gold) pricing.value.gold = props.application.pricing.gold

    // Populate selected subjects for group tutoring
    if (props.application.pricing.silver_subjects && props.application.pricing.silver_subjects.length > 0) {
      // Handle both old format (array of IDs) and new format (array of objects with level_ids)
      if (typeof props.application.pricing.silver_subjects[0] === 'object') {
        // New format: array of objects with id, name, level_ids
        // Enrich with full subject data including color
        selectedGroupSubjects.value = props.application.pricing.silver_subjects.map(savedSubject => {
          const fullSubject = props.userSubjects.find(s => s.id === savedSubject.id)
          return {
            ...fullSubject, // Get color and other properties from userSubjects
            ...savedSubject, // Override with saved data (id, name, level_ids)
          }
        })
      } else {
        // Old format: array of IDs - convert to new format
        selectedGroupSubjects.value = props.userSubjects.filter(subject =>
          props.application.pricing.silver_subjects.includes(subject.id)
        ).map(subject => ({
          ...subject,
          level_ids: [] // No level data in old format
        }))
      }
    }
  }

  if (props.application.group_availability) {
    selectedGroupSlots.value = props.application.group_availability
  }

  if (props.application.one_to_one_availability) {
    selectedOneOnOneSlots.value = props.application.one_to_one_availability
  }
}

// Populate on mount and when application or userSubjects change
onMounted(() => {
  populateFromApplication()
})

watch(() => props.application, () => {
  populateFromApplication()
}, { deep: true })

watch(() => props.userSubjects, () => {
  populateFromApplication()
}, { deep: true })

// Determine which teaching methods are selected
const selectedMethodNames = computed(() => {
  return props.teachingMethods
    .filter(m => props.teachingMethodIds.includes(m.id))
    .map(m => m.name.toLowerCase())
})

const hasBasicMethods = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('study material') || name.includes('recorded') || name.includes('video')
  )
})

const hasSilverMethods = computed(() => {
  return selectedMethodNames.value.some(name => name.includes('group'))
})

const hasGoldMethods = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('1:1') || name.includes('one-to-one') || name.includes('1-on-1')
  )
})

// Computed: Group slots count
const groupSlotsCount = computed(() => selectedGroupSlots.value.length)

// Computed: 1:1 slots count
const oneOnOneSlotsCount = computed(() => selectedOneOnOneSlots.value.length)

// Handle pricing updates from TieredPricingCards
const handlePricingUpdate = (newPricing) => {
  // Only update pricing for services that are offered
  pricing.value = {
    basic: hasBasicMethods.value ? newPricing.basic : 0,
    silver: hasSilverMethods.value ? newPricing.silver : 0,
    gold: hasGoldMethods.value ? newPricing.gold : 0
  }

  // Clear pricing errors when user adjusts prices
  if (errors.value['pricing.base']) {
    errors.value['pricing.base'] = ''
  }
  if (errors.value['pricing.silver']) {
    errors.value['pricing.silver'] = ''
  }
  if (errors.value['pricing.gold']) {
    errors.value['pricing.gold'] = ''
  }
}

// Handle subject selection updates from TieredPricingCards
const handleSubjectsUpdate = (subjects) => {
  selectedGroupSubjects.value = subjects
  // Clear subject error when user selects subjects
  if (errors.value.subjects) {
    errors.value.subjects = ''
  }
}

// Clear availability errors when user selects slots
watch(selectedGroupSlots, () => {
  if (errors.value.groupAvailability) {
    errors.value.groupAvailability = ''
  }
})

watch(selectedOneOnOneSlots, () => {
  if (errors.value.oneOnOneAvailability) {
    errors.value.oneOnOneAvailability = ''
  }
})

const submitForm = async () => {
  errors.value = {}

  // Validate group subjects selection
  if (hasSilverMethods.value && selectedGroupSubjects.value.length === 0) {
    errors.value.subjects = 'Please select at least one subject for group tutoring'

    // Scroll to error message
    setTimeout(() => {
      const errorElement = document.getElementById('subject-error')
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  // Validate each subject-level combination has at least one slot
  if (hasSilverMethods.value) {
    // Check if each subject-level has at least one slot
    const subjectLevelSlotMap = new Map()
    selectedGroupSlots.value.forEach(slot => {
      const key = `${slot.subject_id}-${slot.level_id}`
      subjectLevelSlotMap.set(key, (subjectLevelSlotMap.get(key) || 0) + 1)
    })

    // Build list of expected subject-level combinations
    const expectedCombinations = []
    selectedGroupSubjects.value.forEach(subject => {
      subject.level_ids?.forEach(levelId => {
        expectedCombinations.push(`${subject.id}-${levelId}`)
      })
    })

    const missingSlots = expectedCombinations.filter(combo => !subjectLevelSlotMap.has(combo))

    if (missingSlots.length > 0) {
      errors.value.groupAvailability = 'Please select at least one time slot for each group tutoring course'

      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.getElementById('availability-error')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }

    // Validate no subject-level combo exceeds max sessions (0 = unlimited)
    const exceededCombos = []
    if (props.maxGroupSessionsPerCourse > 0) {
      subjectLevelSlotMap.forEach((count, key) => {
        if (count > props.maxGroupSessionsPerCourse) {
          exceededCombos.push(key)
        }
      })
    }

    if (exceededCombos.length > 0) {
      errors.value.groupAvailability = `Each course can have a maximum of ${props.maxGroupSessionsPerCourse} session(s). Please remove some slots.`

      setTimeout(() => {
        const errorElement = document.getElementById('availability-error')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
  }

  // Validate 1:1 session availability
  if (hasGoldMethods.value && selectedOneOnOneSlots.value.length === 0) {
    errors.value.oneOnOneAvailability = 'Please select at least one 1:1 tutoring session slot'

    // Scroll to error message
    setTimeout(() => {
      const errorElement = document.getElementById('one-on-one-availability-error')
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  // Validate 1:1 sessions don't exceed max
  if (hasGoldMethods.value && props.maxOneToOneSessions > 0 && selectedOneOnOneSlots.value.length > props.maxOneToOneSessions) {
    errors.value.oneOnOneAvailability = `You can select a maximum of ${props.maxOneToOneSessions} 1:1 session slot(s). Please remove some slots.`

    setTimeout(() => {
      const errorElement = document.getElementById('one-on-one-availability-error')
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true

  // Prepare form data - use JSON structure for pricing
  const formData = {
    pricing: {
      base: hasBasicMethods.value ? pricing.value.basic : null,
      silver: hasSilverMethods.value ? pricing.value.silver : null,
      gold: hasGoldMethods.value ? pricing.value.gold : null,
      silver_subjects: hasSilverMethods.value ? selectedGroupSubjects.value.map(s => ({
        id: s.id,
        name: s.name,
        level_ids: s.level_ids || []
      })) : []
    },
    availability: {
      group_sessions: hasSilverMethods.value ? selectedGroupSlots.value : [],
      one_on_one_sessions: hasGoldMethods.value ? selectedOneOnOneSlots.value : [],
    },
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/pricing-availability', {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}

const saveDraft = async () => {
  loading.value = true
  errors.value = {}

  // Prepare form data
  const formData = {
    pricing: {
      base: hasBasicMethods.value ? pricing.value.basic : null,
      silver: hasSilverMethods.value ? pricing.value.silver : null,
      gold: hasGoldMethods.value ? pricing.value.gold : null,
      silver_subjects: hasSilverMethods.value ? selectedGroupSubjects.value.map(s => ({
        id: s.id,
        name: s.name,
        level_ids: s.level_ids || []
      })) : []
    },
    availability: {
      group_sessions: hasSilverMethods.value ? selectedGroupSlots.value : [],
      one_on_one_sessions: hasGoldMethods.value ? selectedOneOnOneSlots.value : [],
    },
    is_draft: true // Indicate this is a draft save
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/pricing-availability', {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false

    // Show success message
    errors.value = { success: 'Draft saved successfully!' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const goBack = () => {
  emit('back')
}
</script>

<template>
  <main class="max-w-7xl mx-auto px-0 md:px-8 py-8">
    <!-- Success Message -->
    <div v-if="errors.success" class="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
      <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
        <span class="material-symbols-outlined text-white text-xl">check_circle</span>
      </div>
      <div>
        <p class="text-green-900 font-semibold">Success</p>
        <p class="text-green-700 text-sm">{{ errors.success }}</p>
      </div>
    </div>

    <!-- General Error Message -->
    <div v-if="errors.general" class="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
      <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
        <span class="material-symbols-outlined text-white text-xl">error</span>
      </div>
      <div>
        <p class="text-red-900 font-semibold">Error</p>
        <p class="text-red-700 text-sm">{{ errors.general }}</p>
      </div>
    </div>

    <!-- Intro Section -->
    <section id="intro-section" class="mb-8">
      <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 md:p-8 border border-green-100">
        <div class="flex items-start gap-4">
          <div class="w-14 h-14 bg-brand-green rounded-xl flex items-center justify-center shrink-0">
            <span class="material-symbols-outlined text-white text-xl">currency_pound</span>
          </div>
          <div class="flex-1">
            <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">Tiered Pricing Plans</h2>
            <p class="hidden md:block text-gray-600 text-base leading-relaxed">
              Set competitive prices for your three subscription tiers. Students can choose the level of support they need.
              Adjust the sliders to set your weekly pricing for each plan. Use the checkboxes to enable or disable specific tiers.
            </p>
            <p class="md:hidden text-gray-600 text-base leading-relaxed">
                Set competitive prices for your three subscription tiers. Students can choose the level of support they need.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Subject Selection Error -->
    <div v-if="errors.subjects" id="subject-error" class="mb-6">
      <div class="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ errors.subjects }}</p>
            <p class="text-red-700 text-sm mt-1">Please select subjects in the Group Tuition card below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tiered Pricing Cards -->
    <TieredPricingCards
      :pricing-limits="pricingLimits"
      :user-subjects="userSubjects"
      :user-levels="userLevels"
      :initial-selected-subjects="selectedGroupSubjects"
      :initial-pricing="pricing"
      :has-basic-methods="hasBasicMethods"
      :has-silver-methods="hasSilverMethods"
      :has-gold-methods="hasGoldMethods"
      :errors="errors"
      @update:pricing="handlePricingUpdate"
      @update:subjects="handleSubjectsUpdate"
    />

    <!-- Revenue Calculator -->
    <RevenueCalculator
      :base-plan-price="pricing.basic"
      :silver-plan-price="pricing.silver"
      :gold-plan-price="pricing.gold"
      :base-plan-offered="hasBasicMethods"
      :silver-plan-offered="hasSilverMethods"
      :gold-plan-offered="hasGoldMethods"
    />

    <!-- Weekly Availability -->
    <section id="weekly-availability" class="mb-8" v-if="hasSilverMethods || hasGoldMethods">
      <div class="bg-white rounded-2xl p-4 md:p-8 border border-gray-200">
        <div class="flex flex-col md:flex-col md:items-center justify-between mb-6 gap-4">
          <div>
            <h3 class="text-xl font-bold text-gray-900">Weekly Availability</h3>
            <p class="hidden md:block text-sm text-gray-500 mt-1">Set your availability for group tutoring and 1:1 sessions</p>
            <p class="md:hidden text-sm text-gray-500 mt-1">Set groups and 1:1 session times</p>
          </div>
          <div class="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
            <span class="material-symbols-outlined text-brand-blue">info</span>
            <span class="text-sm text-gray-700">
              <span class="font-semibold text-brand-blue">{{ groupSlotsCount }}</span> group
              <span v-if="hasSilverMethods && hasGoldMethods"> + </span>
              <span v-if="hasGoldMethods" class="font-semibold text-brand-blue">{{ oneOnOneSlotsCount }}</span>
              <span v-if="hasGoldMethods"> 1:1 slots selected</span>
            </span>
          </div>
        </div>

        <!-- Group Tutoring Sessions -->
        <div v-if="hasSilverMethods" class="mb-8">
          <!-- Error Message -->
          <div v-if="errors.groupAvailability" id="availability-error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-red-600 mt-0.5">error</span>
              <p class="text-red-600 font-medium text-sm">{{ errors.groupAvailability }}</p>
            </div>
          </div>

          <!-- Header -->
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100">
              <span class="material-symbols-outlined text-purple-600">groups</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Group Tutoring Sessions</h4>
              <p class="text-xs text-gray-500">{{ maxGroupSessionsPerCourse > 0 ? `Select up to ${maxGroupSessionsPerCourse} time slot(s) per course for weekly group sessions` : 'Select time slots for weekly group sessions (you can select multiple)' }}</p>
            </div>
          </div>

          <!-- Subject-Level Selector -->
          <SubjectLevelSelector
            :selected-subjects="selectedGroupSubjects"
            :levels="userLevels"
            :selected-subject-level="currentSubjectLevel"
            @update:selected-subject-level="currentSubjectLevel = $event"
          />

          <AvailabilityScheduler
            title=""
            description=""
            icon="groups"
            icon-bg-color="bg-purple-100"
            icon-text-color="text-purple-600"
            :selected-slots="selectedGroupSlots"
            @update:selected-slots="selectedGroupSlots = $event"
            :disabled-slots="selectedOneOnOneSlots"
            :max-slots-per-group="maxGroupSessionsPerCourse"
            :multi-select="true"
            selected-color="purple"
            :is-group-calendar="true"
            :current-subject-level="currentSubjectLevel"
            :show-slot-details="true"
            :hide-header="true"
            :subjects="selectedGroupSubjects"
          />
        </div>

        <!-- 1:1 Tutoring Sessions -->
        <div v-if="hasGoldMethods" class="mb-8">
          <!-- Error Message -->
          <div v-if="errors.oneOnOneAvailability" id="one-on-one-availability-error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-red-600 mt-0.5">error</span>
              <p class="text-red-600 font-medium text-sm">{{ errors.oneOnOneAvailability }}</p>
            </div>
          </div>

          <AvailabilityScheduler
            title="1:1 Tutoring Sessions"
            :description="maxOneToOneSessions > 0 ? `Select up to ${maxOneToOneSessions} time slot(s) for individual tutoring` : 'Select available time slots for individual tutoring (excluding group session times)'"
            icon="person"
            icon-bg-color="bg-yellow-100"
            icon-text-color="text-yellow-600"
            :selected-slots="selectedOneOnOneSlots"
            @update:selected-slots="selectedOneOnOneSlots = $event"
            :disabled-slots="selectedGroupSlots"
            :multi-select="true"
            selected-color="yellow"
            :show-slot-details="true"
            :max-total-slots="maxOneToOneSessions"
          />
        </div>
      </div>
    </section>

    <!-- Tips Section -->
    <section id="tips-section" class="mb-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <span class="material-symbols-outlined text-white text-xl">lightbulb</span>
            </div>
            <div>
              <h4 class="text-lg font-bold text-gray-900 mb-2">Pricing Tips</h4>
              <ul class="space-y-2 text-sm text-gray-700">
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-blue-600 mt-1 shrink-0">check</span>
                  <span>Research competitor pricing in your subject area</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-blue-600 mt-1 shrink-0">check</span>
                  <span>Consider your experience level and qualifications</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-blue-600 mt-1 shrink-0">check</span>
                  <span>Start competitive and adjust based on demand</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shrink-0">
              <span class="material-symbols-outlined text-white text-xl">star</span>
            </div>
            <div>
              <h4 class="text-lg font-bold text-gray-900 mb-2">Value Proposition</h4>
              <ul class="space-y-2 text-sm text-gray-700">
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-purple-600 mt-1 shrink-0">check</span>
                  <span>Highlight your unique teaching methods</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-purple-600 mt-1 shrink-0">check</span>
                  <span>Emphasize personalized attention in higher tiers</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="material-symbols-outlined text-purple-600 mt-1 shrink-0">check</span>
                  <span>Offer clear value differentiation between tiers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

  </main>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="true"
    primary-button-text="Continue to Bank Details"
    primary-button-loading-text="Saving..."
    @previous="goBack"
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
